import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, LogOut, Users, Building2, Calendar, IndianRupee,
  Eye, Edit, Trash2, CheckCircle, Search, CreditCard
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Edit agency dialog
  const [editingAgency, setEditingAgency] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Admin auth uses httpOnly cookies - no localStorage needed for security
  const getAuthConfig = () => {
    return { withCredentials: true };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const authConfig = getAuthConfig();
        const [statsRes, agenciesRes, usersRes, bookingsRes, payoutsRes] = await Promise.all([
          axios.get(`${API}/admin/dashboard`, authConfig),
          axios.get(`${API}/admin/agencies`, authConfig),
          axios.get(`${API}/admin/users`, authConfig),
          axios.get(`${API}/admin/bookings`, authConfig),
          axios.get(`${API}/admin/payouts`, authConfig)
        ]);
        
        setStats(statsRes.data);
        setAgencies(agenciesRes.data);
        setUsers(usersRes.data);
        setBookings(bookingsRes.data);
        setPayouts(payoutsRes.data);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        if (error.response?.status === 401) {
          toast.error('Session expired');
          navigate('/admin');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, getAuthConfig());
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/admin');
  };

  const handleUpdateAgency = async () => {
    try {
      await axios.put(
        `${API}/admin/agencies/${editingAgency.agency_id}`,
        editingAgency,
        getAuthConfig()
      );
      
      setAgencies(agencies.map(a => 
        a.agency_id === editingAgency.agency_id ? editingAgency : a
      ));
      setShowEditDialog(false);
      toast.success('Agency updated');
    } catch (error) {
      console.error('Error updating agency:', error);
      toast.error('Failed to update agency');
    }
  };

  const handleDeleteAgency = async (agencyId) => {
    if (!window.confirm('Are you sure you want to delete this agency?')) return;
    
    try {
      await axios.delete(`${API}/admin/agencies/${agencyId}`, getAuthConfig());
      setAgencies(agencies.filter(a => a.agency_id !== agencyId));
      toast.success('Agency deleted');
    } catch (error) {
      console.error('Error deleting agency:', error);
      toast.error('Failed to delete agency');
    }
  };

  const handleCompletePayout = async (bookingId) => {
    try {
      await axios.post(
        `${API}/admin/payouts/${bookingId}/complete`,
        {},
        getAuthConfig()
      );
      
      setPayouts(payouts.map(p => 
        p.booking_id === bookingId ? { ...p, payout_status: 'completed' } : p
      ));
      toast.success('Payout marked as completed');
    } catch (error) {
      console.error('Error completing payout:', error);
      toast.error('Failed to complete payout');
    }
  };

  const handleMarkAsPaid = async (bookingId) => {
    if (!window.confirm('Are you sure you want to mark this booking as paid? Make sure you have verified the payment in Stripe.')) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/bookings/${bookingId}/mark-paid`,
        {},
        getAuthConfig()
      );
      
      // Update local state
      setBookings(bookings.map(b => 
        b.booking_id === bookingId ? { ...b, payment_status: 'paid', status: 'confirmed' } : b
      ));
      
      // Refresh stats
      const statsRes = await axios.get(`${API}/admin/dashboard`, getAuthConfig());
      setStats(statsRes.data);
      
      // Refresh payouts
      const payoutsRes = await axios.get(`${API}/admin/payouts`, getAuthConfig());
      setPayouts(payoutsRes.data);
      
      toast.success('Booking marked as paid');
    } catch (error) {
      console.error('Error marking as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const filteredAgencies = agencies.filter(a => 
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6" />
            <span className="font-display text-xl font-bold">Admin Panel</span>
          </div>
          <Button 
            variant="ghost"
            onClick={handleLogout}
            className="text-white hover:text-slate-300 hover:bg-slate-800"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs uppercase font-bold">Users</span>
            </div>
            <p className="font-display text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Building2 className="w-4 h-4" />
              <span className="text-xs uppercase font-bold">Agencies</span>
            </div>
            <p className="font-display text-2xl font-bold text-slate-900">{stats?.total_agencies || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs uppercase font-bold">Bookings</span>
            </div>
            <p className="font-display text-2xl font-bold text-slate-900">{stats?.total_bookings || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs uppercase font-bold">Paid</span>
            </div>
            <p className="font-display text-2xl font-bold text-emerald-600">{stats?.total_paid_bookings || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <IndianRupee className="w-4 h-4" />
              <span className="text-xs uppercase font-bold">Revenue</span>
            </div>
            <p className="font-display text-2xl font-bold text-emerald-600">{formatPrice(stats?.total_revenue || 0)}</p>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <CreditCard className="w-4 h-4" />
              <span className="text-xs uppercase font-bold">Pending Payouts</span>
            </div>
            <p className="font-display text-2xl font-bold text-amber-600">{stats?.pending_payouts_count || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white border border-slate-200">
          <TabsList className="w-full border-b border-slate-200 rounded-none bg-transparent p-0 h-auto flex">
            <TabsTrigger value="overview" className="flex-1 rounded-none px-4 py-3 data-[state=active]:bg-slate-50 data-[state=active]:border-b-2 data-[state=active]:border-slate-900">
              Overview
            </TabsTrigger>
            <TabsTrigger value="agencies" className="flex-1 rounded-none px-4 py-3 data-[state=active]:bg-slate-50 data-[state=active]:border-b-2 data-[state=active]:border-slate-900">
              Agencies ({agencies.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="flex-1 rounded-none px-4 py-3 data-[state=active]:bg-slate-50 data-[state=active]:border-b-2 data-[state=active]:border-slate-900">
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex-1 rounded-none px-4 py-3 data-[state=active]:bg-slate-50 data-[state=active]:border-b-2 data-[state=active]:border-slate-900">
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="payouts" className="flex-1 rounded-none px-4 py-3 data-[state=active]:bg-slate-50 data-[state=active]:border-b-2 data-[state=active]:border-slate-900">
              Payouts
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Recent Bookings</h3>
                <div className="space-y-2">
                  {bookings.slice(0, 5).map(booking => (
                    <div key={booking.booking_id} className="border border-slate-200 p-3 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">{booking.user_name}</span>
                        <span className="text-emerald-600">{formatPrice(booking.amount)}</span>
                      </div>
                      <p className="text-slate-500">{booking.agency_name} - {booking.service_type}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Recent Agencies</h3>
                <div className="space-y-2">
                  {agencies.slice(0, 5).map(agency => (
                    <div key={agency.agency_id} className="border border-slate-200 p-3 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">{agency.name}</span>
                        <span className={agency.verified ? 'text-emerald-600' : 'text-slate-400'}>
                          {agency.verified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                      <p className="text-slate-500">{agency.city}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Agencies Tab */}
          <TabsContent value="agencies" className="p-0">
            <div className="p-4 border-b border-slate-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search agencies..."
                  className="pl-10 rounded-none border-slate-200"
                />
              </div>
            </div>
            <div className="divide-y divide-slate-200">
              {filteredAgencies.map(agency => (
                <div key={agency.agency_id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-display font-bold text-slate-900">{agency.name}</h4>
                        {agency.verified && (
                          <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 border border-emerald-200">
                            Verified
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{agency.city} • {agency.phone}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Bank: {agency.bank_account_name || 'Not provided'} 
                        {agency.bank_account_number && ` - ****${agency.bank_account_number.slice(-4)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setEditingAgency({...agency}); setShowEditDialog(true); }}
                        className="rounded-none border-slate-200"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAgency(agency.agency_id)}
                        className="rounded-none border-rose-200 text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="p-0">
            <div className="divide-y divide-slate-200">
              {users.map(user => (
                <div key={user.user_id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <Users className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{user.name}</h4>
                      <p className="text-sm text-slate-500">{user.email} • {user.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="p-0">
            <div className="divide-y divide-slate-200">
              {bookings.map(booking => (
                <div key={booking.booking_id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-display font-bold text-slate-900">{booking.user_name}</h4>
                        <span className={`text-xs px-2 py-0.5 border ${
                          booking.payment_status === 'paid' 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>
                          {booking.payment_status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">{booking.agency_name}</p>
                      <p className="text-sm text-slate-600 mt-1">{booking.service_type} - {booking.date}</p>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <p className="font-display text-lg font-bold text-slate-900">{formatPrice(booking.amount)}</p>
                      <p className="text-xs text-slate-500">{booking.status}</p>
                      {booking.payment_status !== 'paid' && (
                        <Button
                          size="sm"
                          onClick={() => handleMarkAsPaid(booking.booking_id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                          data-testid={`mark-paid-${booking.booking_id}`}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Mark as Paid
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts" className="p-0">
            <div className="divide-y divide-slate-200">
              {payouts.map(payout => (
                <div key={payout.booking_id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-display font-bold text-slate-900">{payout.agency_name}</h4>
                      <p className="text-sm text-slate-500">{payout.service_type} - {payout.booking_date}</p>
                      <div className="text-sm text-slate-600 mt-2">
                        <p>Bank: {payout.bank_account_name || 'Not provided'}</p>
                        {payout.bank_account_number && <p>A/C: ****{payout.bank_account_number.slice(-4)}</p>}
                        {payout.bank_ifsc && <p>IFSC: {payout.bank_ifsc}</p>}
                        {payout.upi_id && <p>UPI: {payout.upi_id}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Amount: {formatPrice(payout.amount)}</p>
                      <p className="text-sm text-slate-500">Platform Fee (10%): {formatPrice(payout.platform_fee)}</p>
                      <p className="font-display text-lg font-bold text-emerald-600">Net: {formatPrice(payout.net_amount)}</p>
                      
                      {payout.payout_status !== 'completed' ? (
                        <Button
                          size="sm"
                          onClick={() => handleCompletePayout(payout.booking_id)}
                          className="mt-2 rounded-none bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Paid
                        </Button>
                      ) : (
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 border border-emerald-200">
                          Completed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {payouts.length === 0 && (
                <div className="p-12 text-center text-slate-500">
                  No pending payouts
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Agency Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="rounded-none border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-slate-900">
              Edit Agency
            </DialogTitle>
          </DialogHeader>
          
          {editingAgency && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 mb-1 block">Name</label>
                <Input
                  value={editingAgency.name || ''}
                  onChange={(e) => setEditingAgency({...editingAgency, name: e.target.value})}
                  className="rounded-none border-slate-200"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 mb-1 block">City</label>
                <Input
                  value={editingAgency.city || ''}
                  onChange={(e) => setEditingAgency({...editingAgency, city: e.target.value})}
                  className="rounded-none border-slate-200"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 mb-1 block">Phone</label>
                <Input
                  value={editingAgency.phone || ''}
                  onChange={(e) => setEditingAgency({...editingAgency, phone: e.target.value})}
                  className="rounded-none border-slate-200"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingAgency.verified || false}
                  onChange={(e) => setEditingAgency({...editingAgency, verified: e.target.checked})}
                  className="w-4 h-4"
                />
                <label className="text-sm text-slate-700">Verified Agency</label>
              </div>
              <Button 
                onClick={handleUpdateAgency}
                className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-800"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
